import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  depositUnaccountedAmount,
  isDepositAbandoned,
  isDepositFullyAccounted,
  recordWasAdjusted,
} from '@dill/dill-shared';
import type { CashReceipt, RemittanceRecord } from '@dill/dill-shared';
import { toast } from 'react-toastify';
import { PostStatusBadge } from '../components/PostStatusBadge';
import { DepositStatusBadge } from '../components/DepositStatusBadge';
import { PostMethodBadge } from '../components/PostMethodBadge';
import { ConfidenceFactorList } from '../components/ConfidenceFactorList';
import { RecordAllocationTable } from './RecordAllocationTable';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { retryFailedPost } from '../../../../redux/globalSlices/cashApplicationSlice';
import {
  fetchCashReceiptsThunk,
  fetchRemittanceRecordsThunk,
  retryDepositThunk,
  retryPostThunk,
} from '../../../../redux/services/cashApplicationService';
import { DillModal } from '../../../general/Dill';
import { formatCurrency, formatDateTime } from '../utils';
import {
  ArrowClockwise,
  CaretDown,
  CaretRight,
  CheckCircle,
  WarningCircle,
  Clock,
  Lightning,
  Bank,
  Receipt,
  Info,
} from 'phosphor-react';

interface ReceiptDetailModalProps {
  receipt: CashReceipt | null;
  onClose: () => void;
}

export const ReceiptDetailModal = ({ receipt, onClose }: ReceiptDetailModalProps) => {
  const dispatch = useAppDispatch();
  const supplierId = useAppSelector((state) => state.auth.currentSupplier?.id);
  const postHistory = useAppSelector((state) => state.cashApplication.postHistory);
  const recordsById = useAppSelector((state) => state.cashApplication.remittanceRecordsById);
  const bankTransactions = useAppSelector((state) => state.cashApplication.bankTransactions);

  /** The payments this receipt covers, and the ids of any that have not loaded. */
  const { records, missingIds } = useMemo(() => {
    const found: RemittanceRecord[] = [];
    const missing: string[] = [];
    for (const id of receipt?.remittanceIds ?? []) {
      const record = recordsById[id];
      if (record) {
        found.push(record);
      } else {
        missing.push(id);
      }
    }
    return { records: found, missingIds: missing };
  }, [receipt?.remittanceIds, recordsById]);

  /**
   * Which payment cards are open.
   *
   * All of them when there is one, only the failures otherwise — a twelve-payment receipt is
   * unreadable fully expanded, and the ones needing attention are the ones worth opening.
   */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!receipt) return;
    setExpandedIds(
      new Set(
        records.length === 1
          ? records.map((record) => record.id)
          : records.filter((record) => record.erpPostStatus === 'FAILED').map((record) => record.id)
      )
    );
    // Keyed on the receipt, not on `records`: re-running this on every poll would slam shut a card
    // the operator had just opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt?.id]);

  const toggleRecord = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * Ask the ERP again, against this same receipt.
   *
   * The optimistic flip to POSTING is dispatched first so the modal responds immediately — it also
   * hides this button, which is the point: the error block only exists while there is an error, and
   * the header badge takes over with its spinner. Whatever the worker settles on arrives on the
   * next re-fetch — the two below, or Refresh in Post History — which the receipt reads from live.
   */
  const handleRetry = useCallback(async () => {
    if (!receipt || !supplierId) return;
    const receiptId = receipt.id;
    dispatch(retryFailedPost(receiptId));
    const result = await dispatch(retryPostThunk({ supplierId, receiptId }));
    if (retryPostThunk.rejected.match(result)) {
      toast.error(`${result.payload ?? 'Could not retry the post'}`, { autoClose: 5000 });
    }
    dispatch(fetchCashReceiptsThunk({ supplierId }));
    // The per-payment outcome lives on the records, so without this the cards below would still
    // show the old failure after a successful retry.
    dispatch(fetchRemittanceRecordsThunk({ supplierId }));
  }, [dispatch, receipt?.id, supplierId]);

  /**
   * Ask for the deposit again, without touching the payments.
   *
   * Only ever needed after a deposit that failed on its own — settlement queues the first attempt,
   * and there is no second settlement to queue another. No optimistic flip: the server writes
   * PENDING before it queues the job, and the re-fetch below picks that up.
   */
  const handleRetryDeposit = useCallback(async () => {
    if (!receipt || !supplierId) return;
    const result = await dispatch(retryDepositThunk({ supplierId, receiptId: receipt.id }));
    if (retryDepositThunk.rejected.match(result)) {
      toast.error(`${result.payload ?? 'Could not retry the deposit'}`, { autoClose: 5000 });
    }
    dispatch(fetchCashReceiptsThunk({ supplierId }));
  }, [dispatch, receipt?.id, supplierId]);

  /**
   * Whether a deposit is stuck in a way an operator can do something about.
   *
   * FAILED is the obvious one. PENDING belongs here too once it is old enough: it is written before
   * the job is queued, so it also describes a worker that was killed, and settlement — the only
   * thing that queues a deposit on its own — looks at WAITING and FAILED alone. Without this the
   * receipt would read "Depositing" forever with nothing offering to try again. `isDepositAbandoned`
   * is shared with the endpoint, so the button never appears on something it would refuse.
   */
  const isDepositRecoverable = useMemo(() => {
    if (!receipt) {
      return false;
    }
    if (receipt.depositStatus === 'FAILED') {
      return true;
    }
    return isDepositAbandoned(receipt);
  }, [receipt?.depositStatus, receipt?.depositQueuedAt]);

  /** The credit whose deposit covers this receipt, for the shortfall that explains a wait. */
  const deposit = useMemo(
    () => bankTransactions.find((txn) => txn.id === receipt?.depositBankRecordId) ?? null,
    [bankTransactions, receipt?.depositBankRecordId]
  );

  /**
   * How much of the credit's deposit is not this receipt.
   *
   * Post History bands a receipt with the other one on its credit, but a receipt opened from a
   * search may have no band on screen — so the fact has to be here too, or the deposit id looks
   * like it belongs to this receipt alone and the amounts appear not to add up.
   */
  const shared = useMemo(() => {
    const key = receipt?.erpDepositId || receipt?.depositBankRecordId;
    if (!key) {
      return { receipts: 1, payments: receipt?.remittanceIds.length ?? 0 };
    }
    const siblings = postHistory.filter(
      (other) => (other.erpDepositId || other.depositBankRecordId) === key
    );
    return {
      receipts: siblings.length,
      payments: siblings.reduce((sum, other) => sum + other.remittanceIds.length, 0),
    };
  }, [postHistory, receipt?.erpDepositId, receipt?.depositBankRecordId, receipt?.remittanceIds]);

  if (!receipt) return null;

  const isPartial = receipt.postStatus === 'PARTIAL';
  const failedRecords = records.filter((record) => record.erpPostStatus === 'FAILED');
  const retriable = receipt.postStatus === 'FAILED' || isPartial;
  const shortfall =
    deposit && !isDepositFullyAccounted(deposit) ? depositUnaccountedAmount(deposit) : 0;

  return (
    <DillModal
      open={receipt !== null}
      onClose={onClose}
      title="Receipt Details"
      subtitle={`${receipt.remittanceIds.length} payment${
        receipt.remittanceIds.length === 1 ? '' : 's'
      } — ${formatCurrency(receipt.amount)}`}
      maxWidth="680px">
      <div className="space-y-5">
        {/* ── Header Summary ───────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className={
                'flex items-center justify-center w-10 h-10 rounded-xl ' +
                (receipt.postStatus === 'SUCCESSFUL'
                  ? 'bg-emerald-50'
                  : receipt.postStatus === 'FAILED'
                  ? 'bg-red-50'
                  : isPartial
                  ? 'bg-orange-50'
                  : 'bg-amber-50')
              }>
              {receipt.postStatus === 'SUCCESSFUL' ? (
                <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              ) : receipt.postStatus === 'FAILED' ? (
                <WarningCircle size={20} weight="fill" className="text-red-500" />
              ) : isPartial ? (
                <WarningCircle size={20} weight="fill" className="text-orange-500" />
              ) : (
                <Clock size={20} weight="fill" className="text-amber-500" />
              )}
            </div>
            <div>
              {/* Only one payment has a payer worth naming here. Several are named on their own
                  cards below, and "3 payers" in the header would be a worse answer than none. */}
              <p className="text-sm font-semibold text-gray-900">
                {records.length === 1
                  ? records[0].payerName ?? 'Unknown payer'
                  : `${receipt.remittanceIds.length} payments`}
              </p>
              <p className="text-xs text-gray-500">
                {receipt.postedAt ? formatDateTime(receipt.postedAt) : 'Not yet posted'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-900 tabular-nums">
              {formatCurrency(receipt.amount)}
            </p>
            <PostStatusBadge status={receipt.postStatus} title={receipt.postError} />
          </div>
        </div>

        {/* ── Quick Info Row ────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
            <Lightning size={14} className="text-gray-400" />
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                Method
              </p>
              <PostMethodBadge method={receipt.postMethod} />
            </div>
          </div>
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
            <Bank size={14} className="text-gray-400" />
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">ERP</p>
              <p className="text-xs font-medium text-gray-900">{receipt.erpType ?? '—'}</p>
            </div>
          </div>
          {/*
            Deposit, where Settlement used to be. Settlement is no longer on the receipt, and it is
            implied by the receipt existing at all: a payment the bank has not confirmed is not
            postable. The deposit is the open question worth a tile.
          */}
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
            <Receipt size={14} className="text-gray-400" />
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                Deposit
              </p>
              <div className="flex items-center gap-1.5 min-w-0">
                {receipt.erpDepositId && (
                  <span className="font-mono text-xs text-gray-700 truncate">
                    {receipt.erpDepositId}
                  </span>
                )}
                <DepositStatusBadge
                  status={receipt.depositStatus}
                  // The reason a WAITING deposit is waiting, which is often a payment on the same
                  // credit that has not posted rather than a credit that has not arrived.
                  title={receipt.depositError ?? receipt.depositWaitingReason}
                />
              </div>
            </div>
          </div>
        </div>

        {/* The deposit is one document for the whole credit, so its amount is not this receipt's. */}
        {shared.receipts > 1 && (
          <p className="text-[11px] text-gray-500">
            This deposit covers {shared.payments} payment{shared.payments === 1 ? '' : 's'} across{' '}
            {shared.receipts} receipts, so its total in the ERP is more than this receipt.
          </p>
        )}

        {/* ── Payments ──────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <Info size={12} className="text-blue-500" />
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-600">
              Payments
            </h4>
            {isPartial && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200">
                {records.length - failedRecords.length} of {receipt.remittanceIds.length} posted
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {records.map((record) => {
              const isOpen = expandedIds.has(record.id);
              const failed = record.erpPostStatus === 'FAILED';
              return (
                <div
                  key={record.id}
                  className={
                    'rounded-lg border ' +
                    (failed ? 'border-red-200 bg-red-50/30' : 'border-gray-200')
                  }>
                  <button
                    type="button"
                    onClick={() => toggleRecord(record.id)}
                    aria-expanded={isOpen}
                    className="flex items-center w-full gap-2 px-3 py-2 text-left bg-transparent">
                    {isOpen ? (
                      <CaretDown size={12} weight="bold" className="text-gray-400 shrink-0" />
                    ) : (
                      <CaretRight size={12} weight="bold" className="text-gray-400 shrink-0" />
                    )}
                    <span className="flex-1 min-w-0 text-xs font-medium text-gray-900 truncate">
                      {record.payerName ?? 'Unknown payer'}
                      {record.checkNumber && (
                        <span className="ml-1.5 font-normal text-gray-400">
                          #{record.checkNumber}
                        </span>
                      )}
                    </span>
                    {recordWasAdjusted(record) && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                        Edited
                      </span>
                    )}
                    <span className="text-xs font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(record.amount)}
                    </span>
                    <PostStatusBadge
                      status={record.erpPostStatus ?? receipt.postStatus}
                      title={record.erpPostError}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-3 pb-3 space-y-3">
                      <RecordAllocationTable record={record} />

                      <dl className="space-y-1.5">
                        {/* One payment can produce two documents in the ERP — the amount applied to
                            invoices and, separately, the remainder left on the customer's account.
                            Both are listed because the deposit only reconciles when read together. */}
                        {(record.erpAppliedPaymentIds ?? []).length > 0 && (
                          <div className="flex items-center justify-between">
                            <dt className="text-xs text-gray-500">Applied Payment</dt>
                            <dd className="font-mono text-xs text-gray-700">
                              {(record.erpAppliedPaymentIds ?? []).join(', ')}
                            </dd>
                          </div>
                        )}
                        {record.erpOpenPaymentId && (
                          <div className="flex items-center justify-between">
                            <dt className="text-xs text-gray-500">Open Credit</dt>
                            <dd className="font-mono text-xs text-gray-700">
                              {record.erpOpenPaymentId}
                            </dd>
                          </div>
                        )}
                      </dl>

                      {record.erpPostError && (
                        <p className="text-xs text-red-600">{record.erpPostError}</p>
                      )}

                      {record.matchConfidenceFactors.length > 0 && (
                        <div className="p-3 rounded-lg bg-gray-50">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 mb-1.5">
                            Match confidence at post
                          </p>
                          <ConfidenceFactorList factors={record.matchConfidenceFactors} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Never silently dropped: the receipt says it covers this payment, so its absence is
                a fact about the page rather than about the post. */}
            {missingIds.map((id) => (
              <div
                key={id}
                className="flex items-center justify-between gap-2 px-3 py-2 border border-gray-200 border-dashed rounded-lg">
                <span className="text-xs italic text-gray-400">
                  Payment details not loaded
                  <span className="ml-1.5 font-mono text-[10px] text-gray-300">
                    {id.slice(0, 8)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    supplierId && dispatch(fetchRemittanceRecordsThunk({ supplierId }))
                  }
                  className="text-xs font-medium text-blue-600 hover:text-blue-700">
                  Reload payments
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Metadata ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400">
              Posting
            </h4>
            <dl className="space-y-1.5">
              {receipt.postedBy && (
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500">Posted By</dt>
                  <dd className="text-xs font-medium text-gray-900">{receipt.postedBy}</dd>
                </div>
              )}
              {receipt.postedAt && (
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500">Posted At</dt>
                  <dd className="text-xs text-gray-700 tabular-nums">
                    {formatDateTime(receipt.postedAt)}
                  </dd>
                </div>
              )}
              {receipt.depositedAt && (
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500">Deposited At</dt>
                  <dd className="text-xs text-gray-700 tabular-nums">
                    {formatDateTime(receipt.depositedAt)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400">
              Diagnostics
            </h4>
            <dl className="space-y-1.5">
              {receipt.retryCount > 0 && (
                <div className="flex items-center justify-between">
                  <dt className="text-xs text-gray-500">Retries</dt>
                  <dd className="text-xs text-gray-700 tabular-nums">{receipt.retryCount}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* ── Retry the post ────────────────────────────────────────── */}
        {retriable && (
          <div
            className={
              'p-3 rounded-lg border ' +
              (isPartial ? 'bg-orange-50 border-orange-100' : 'bg-red-50 border-red-100')
            }>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p
                  className={
                    'text-[10px] font-semibold uppercase tracking-wider mb-1 ' +
                    (isPartial ? 'text-orange-700' : 'text-red-600')
                  }>
                  {isPartial ? 'Partly posted' : 'Error'}
                </p>
                <p className={'text-xs ' + (isPartial ? 'text-orange-800' : 'text-red-600')}>
                  {receipt.postError ?? 'The post could not be completed.'}
                </p>
                {/* Says what the retry will actually do, which is the thing an operator hesitates
                    over: the payments already in the ERP are not touched. */}
                {isPartial && (
                  <p className="text-[10px] text-orange-700 mt-1">
                    {failedRecords.length} of {receipt.remittanceIds.length} payments could not be
                    posted. Retrying re-sends only those — the{' '}
                    {receipt.remittanceIds.length - failedRecords.length} already applied are
                    untouched.
                  </p>
                )}
              </div>
              <button
                onClick={handleRetry}
                className={
                  'shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-white ring-1 ring-inset transition-colors ' +
                  (isPartial
                    ? 'text-orange-800 ring-orange-600/30 hover:bg-orange-100'
                    : 'text-red-700 ring-red-600/30 hover:bg-red-100')
                }>
                <ArrowClockwise size={13} weight="bold" />
                {isPartial ? 'Retry failed payments' : 'Retry post'}
              </button>
            </div>
          </div>
        )}

        {/**
         * A deposit that has not happened, kept apart from the error block above and coloured as a
         * warning.
         *
         * The distinction is the whole reason this is separate: the cash applied correctly and the
         * invoices are paid. What is wrong is that the payments are still sitting in Undeposited
         * Funds, so the bank reconciliation will not balance until this is retried or done by hand.
         *
         * A credit that is still short gets a sentence instead of a button. That is not something a
         * retry can fix — the server refuses it — and the next step is in Unmatched Deposits.
         */}
        {receipt.postStatus === 'SUCCESSFUL' && shortfall > 0.005 && (
          <div className="p-3 border rounded-lg bg-amber-50 border-amber-100">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
              Waiting on the bank deposit
            </p>
            <p className="text-xs text-amber-700">
              This bank deposit is still short {formatCurrency(shortfall)}. It will be deposited in
              the ERP once the rest of it is linked in Unmatched Deposits.
            </p>
          </div>
        )}

        {receipt.postStatus === 'SUCCESSFUL' && shortfall <= 0.005 && isDepositRecoverable && (
          <div className="p-3 border rounded-lg bg-amber-50 border-amber-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 mb-1">
                  Not deposited
                </p>
                <p className="text-xs text-amber-700">
                  {receipt.depositStatus === 'PENDING'
                    ? 'The deposit was started but never finished.'
                    : receipt.depositError ??
                      'The deposit could not be created in the accounting system.'}
                </p>
                <p className="text-[10px] text-amber-600 mt-1">
                  The payments applied correctly and are still in Undeposited Funds.
                  {/* Says what a retry will actually do: one deposit, for the whole credit. */}
                  {shared.payments > 1 &&
                    ` This deposit covers ${shared.payments} payments, and retrying it covers all of them.`}
                </p>
              </div>
              <button
                onClick={handleRetryDeposit}
                className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-amber-800 bg-white ring-1 ring-inset ring-amber-600/30 hover:bg-amber-100 transition-colors">
                <ArrowClockwise size={13} weight="bold" />
                Retry deposit
              </button>
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};
